import React, { useState, useEffect, useMemo } from 'react';
import { useNavigation } from '../contexts/NavigationProvider';
import { useAuth } from '../contexts/AuthProvider';
import { useOpportunities } from '../contexts/OpportunitiesProvider';
import SparklesIcon from './icons/SparklesIcon';
import UsersIcon from './icons/UsersIcon';
import EyeIcon from './icons/EyeIcon';
import AmplifierIcon from './icons/AmplifierIcon';
import HeartIcon from './icons/HeartIcon';
import CalendarIcon from './icons/CalendarIcon';
import { SeedDashboardContent } from './SeedDashboard';
import * as api from '../services/apiService';
import type { Opportunity } from '../types';

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAY_LABELS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

const ProDashboard: React.FC = () => {
    const { currentUser, managedProfile } = useAuth();
    const { handleViewProfile, handleViewFanList, handleViewEndorserList, handleViewOpportunity } = useNavigation();
    const { opportunities } = useOpportunities();
    const [stats, setStats] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    const effectiveUser = managedProfile ?? currentUser;
    const effectiveHandle = effectiveUser?.handle;

    useEffect(() => {
        api.getProDashboardStats()
            .then(statsData => setStats(statsData))
            .finally(() => setLoading(false));
    }, []);

    const isSeed = currentUser?.isSeed;

    const postedWithDates = useMemo(() =>
        opportunities.filter(o => o.author.handle === effectiveHandle && !!o.startDate),
        [opportunities, effectiveHandle]
    );

    const attendingWithDates = useMemo(() =>
        opportunities.filter(o =>
            (o.attendees || []).some(a => a.handle === effectiveHandle) && !!o.startDate
        ),
        [opportunities, effectiveHandle]
    );

    if (loading) return <div className="p-8 text-center text-brand-muted animate-pulse font-urbanist">Syncing your pro insights...</div>;
    if (!stats) return <div className="p-8 text-center text-red-400 font-urbanist">Failed to load analytics.</div>;

    return (
        <div className="space-y-6 font-urbanist">
            {isSeed && <SeedDashboardContent />}

            <div className="bg-brand-surface rounded-lg p-6 border border-gray-800 shadow-sm">
                <div className="flex items-center space-x-3 mb-2">
                    <SparklesIcon className="h-8 w-8 text-brand-orange" />
                    <div>
                        <h2 className="text-3xl font-extrabold text-white tracking-tight">Pro Analytics</h2>
                        <p className="text-brand-muted text-sm italic">Growth insights for your professional profile.</p>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <StatCard
                    label="Total Fans"
                    value={stats.fanCount || 0}
                    icon={<HeartIcon className="h-6 w-6 text-pink-500 fill-current"/>}
                    subtext="Tap to see full list"
                    onClick={effectiveUser ? () => handleViewFanList(effectiveUser) : undefined}
                />
                <StatCard
                    label="Profile Readiness"
                    value={`${stats.profileCompleteness}%`}
                    icon={<UsersIcon className="h-6 w-6 text-brand-orange"/>}
                    subtext="Completeness Score"
                />
                <StatCard
                    label="Endorsements"
                    value={stats.endorsedByCount || 0}
                    icon={<AmplifierIcon className="h-6 w-6 text-green-400"/>}
                    subtext="Tap to see endorsers"
                    onClick={effectiveUser ? () => handleViewEndorserList(effectiveUser as any) : undefined}
                />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Recent Supporters */}
                <div className="bg-brand-surface rounded-lg p-6 border border-gray-800">
                    <h3 className="text-xl font-bold text-white mb-6 tracking-tight">Recent Supporters</h3>
                    {stats.recentFans?.length > 0 ? (
                        <ul className="space-y-4">
                            {stats.recentFans.map((fan: any) => (
                                <li key={fan.handle} onClick={() => handleViewProfile(fan)} className="flex justify-between items-center bg-gray-800/30 p-3 rounded-xl border border-gray-800 cursor-pointer hover:border-brand-orange transition-all group">
                                    <div className="flex items-center gap-3">
                                        <img src={fan.avatarUrl} className="h-10 w-10 rounded-full border border-gray-700" alt="" />
                                        <div>
                                            <p className="font-bold text-white group-hover:text-brand-orange transition-colors">{fan.name}</p>
                                            <p className="text-[10px] text-brand-muted font-mono">@{fan.handle}</p>
                                        </div>
                                    </div>
                                    <span className="text-[10px] bg-pink-500/10 text-pink-500 font-bold px-2 py-0.5 rounded-full">NEW FAN</span>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <div className="text-center py-10">
                            <HeartIcon className="h-12 w-12 text-gray-700 mx-auto mb-2" />
                            <p className="text-brand-muted text-sm italic">No active fans yet. Keep posting!</p>
                        </div>
                    )}
                </div>

                {/* Profile Views */}
                <div className="bg-brand-surface rounded-lg p-6 border border-gray-800">
                    <div className="flex items-center justify-between mb-1">
                        <h3 className="text-xl font-bold text-white tracking-tight">Profile Views</h3>
                        <div className="flex items-center gap-2">
                            <EyeIcon className="h-5 w-5 text-brand-orange" />
                            <span className="text-2xl font-black text-white">{stats.profileViewCount || 0}</span>
                        </div>
                    </div>
                    <p className="text-[10px] text-brand-muted uppercase tracking-widest font-bold mb-4">Last 30 days · Stealth visitors hidden</p>
                    {stats.recentViewers?.length > 0 ? (
                        <ul className="space-y-3">
                            {stats.recentViewers.map((v: any) => (
                                <li key={v.handle} onClick={() => handleViewProfile(v)} className="flex items-center gap-3 bg-gray-800/30 p-3 rounded-xl border border-gray-800 cursor-pointer hover:border-brand-orange/50 transition-all group">
                                    {v.avatarUrl
                                        ? <img src={v.avatarUrl} className="h-9 w-9 rounded-full border border-gray-700 flex-shrink-0" alt="" />
                                        : <div className="h-9 w-9 rounded-full bg-gray-700 border border-gray-600 flex-shrink-0 flex items-center justify-center"><span className="text-xs font-bold text-gray-400">{(v.name || v.handle)[0].toUpperCase()}</span></div>
                                    }
                                    <div className="min-w-0">
                                        <p className="font-bold text-white text-sm truncate group-hover:text-brand-orange transition-colors">{v.name}</p>
                                        <p className="text-[10px] text-brand-muted font-mono">@{v.handle}</p>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <div className="text-center py-8 border-2 border-dashed border-gray-800 rounded-xl">
                            <EyeIcon className="h-10 w-10 text-gray-700 mx-auto mb-2" />
                            <p className="text-brand-muted text-sm italic">No visible visits in the last 30 days.</p>
                        </div>
                    )}
                </div>
            </div>

            <CalendarSection
                postedWithDates={postedWithDates}
                attendingWithDates={attendingWithDates}
                onViewOpportunity={handleViewOpportunity}
            />
        </div>
    );
};

// --- Stat Card ---
const StatCard: React.FC<{
    label: string;
    value: string | number;
    icon: React.ReactNode;
    subtext: string;
    onClick?: () => void;
}> = ({ label, value, icon, subtext, onClick }) => (
    <div
        onClick={onClick}
        className={`bg-brand-surface p-6 rounded-xl border border-gray-800 shadow-sm relative overflow-hidden group transition-all ${onClick ? 'cursor-pointer hover:border-brand-orange/50' : ''}`}
    >
        <div className="absolute top-0 right-0 h-20 w-20 bg-brand-orange/5 rounded-bl-full group-hover:bg-brand-orange/10 transition-colors"></div>
        <div className="flex justify-between items-start relative z-10">
            <div>
                <p className="text-[10px] font-bold text-brand-muted mb-1 tracking-widest">{label}</p>
                <p className="text-4xl font-bold text-white">{value}</p>
            </div>
            <div className="bg-gray-800 p-3 rounded-xl border border-gray-700">{icon}</div>
        </div>
        <p className="text-xs text-brand-muted mt-3 italic">{subtext}</p>
        {onClick && <span className="absolute bottom-3 right-4 text-[9px] font-black text-brand-orange uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity">View all →</span>}
    </div>
);

// --- Calendar Section ---
interface CalendarSectionProps {
    postedWithDates: Opportunity[];
    attendingWithDates: Opportunity[];
    onViewOpportunity: (o: Opportunity) => void;
}

const CalendarSection: React.FC<CalendarSectionProps> = ({ postedWithDates, attendingWithDates, onViewOpportunity }) => {
    const [displayDate, setDisplayDate] = useState(() => {
        const d = new Date();
        return new Date(d.getFullYear(), d.getMonth(), 1);
    });
    const [selectedDay, setSelectedDay] = useState<number | null>(null);

    const year = displayDate.getFullYear();
    const month = displayDate.getMonth();
    const firstDayOfMonth = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const today = new Date();

    const getDayOpps = (day: number) => {
        const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        const posted = postedWithDates.filter(o =>
            o.startDate! <= dateStr && (o.endDate || o.startDate)! >= dateStr
        );
        const attending = attendingWithDates.filter(o =>
            o.startDate! <= dateStr && (o.endDate || o.startDate)! >= dateStr
        );
        return { posted, attending };
    };

    const prevMonth = () => { setDisplayDate(new Date(year, month - 1, 1)); setSelectedDay(null); };
    const nextMonth = () => { setDisplayDate(new Date(year, month + 1, 1)); setSelectedDay(null); };

    const selectedDayOpps = selectedDay ? getDayOpps(selectedDay) : null;
    const allSelectedOpps = selectedDayOpps
        ? [
            ...selectedDayOpps.posted.map(o => ({ ...o, _role: 'posted' as const })),
            ...selectedDayOpps.attending.map(o => ({ ...o, _role: 'attending' as const })),
          ]
        : [];

    return (
        <div className="bg-brand-surface rounded-lg p-6 border border-gray-800">
            <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                    <CalendarIcon className="h-5 w-5 text-brand-orange" />
                    <h3 className="text-xl font-bold text-white tracking-tight">Event Calendar</h3>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={prevMonth} className="p-1.5 hover:bg-gray-700 rounded-lg text-brand-muted hover:text-white transition-colors text-lg leading-none">‹</button>
                    <span className="text-sm font-black text-white uppercase tracking-widest w-36 text-center">
                        {MONTH_NAMES[month]} {year}
                    </span>
                    <button onClick={nextMonth} className="p-1.5 hover:bg-gray-700 rounded-lg text-brand-muted hover:text-white transition-colors text-lg leading-none">›</button>
                </div>
            </div>

            <div className="flex gap-5 mb-4">
                <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-brand-orange inline-block flex-shrink-0" />
                    <span className="text-[10px] text-brand-muted font-bold uppercase tracking-widest">Your events</span>
                </div>
                <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-blue-400 inline-block flex-shrink-0" />
                    <span className="text-[10px] text-brand-muted font-bold uppercase tracking-widest">Attending</span>
                </div>
            </div>

            <div className="grid grid-cols-7 gap-1">
                {DAY_LABELS.map(d => (
                    <div key={d} className="text-center text-[10px] font-black text-brand-muted uppercase tracking-widest py-1">{d}</div>
                ))}
                {/* Empty cells before first day */}
                {Array.from({ length: firstDayOfMonth }).map((_, i) => (
                    <div key={`empty-${i}`} />
                ))}
                {/* Day cells */}
                {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
                    const { posted, attending } = getDayOpps(day);
                    const hasEvents = posted.length > 0 || attending.length > 0;
                    const isToday = today.getFullYear() === year && today.getMonth() === month && today.getDate() === day;
                    const isSelected = selectedDay === day;
                    return (
                        <button
                            key={day}
                            onClick={() => setSelectedDay(isSelected ? null : day)}
                            className={`relative flex flex-col items-center justify-start pt-1.5 pb-2 rounded-lg min-h-[44px] transition-all text-sm font-bold border
                                ${isSelected
                                    ? 'bg-brand-orange/20 border-brand-orange/60 text-white'
                                    : isToday
                                        ? 'bg-gray-700/40 border-gray-600 text-brand-orange'
                                        : hasEvents
                                            ? 'border-transparent hover:bg-gray-800/60 hover:border-gray-700 text-white'
                                            : 'border-transparent hover:bg-gray-800/30 text-gray-400'
                                }`}
                        >
                            <span>{day}</span>
                            {hasEvents && (
                                <div className="flex gap-0.5 mt-0.5">
                                    {posted.length > 0 && <span className="w-1.5 h-1.5 rounded-full bg-brand-orange" />}
                                    {attending.length > 0 && <span className="w-1.5 h-1.5 rounded-full bg-blue-400" />}
                                </div>
                            )}
                        </button>
                    );
                })}
            </div>

            {selectedDay && (
                <div className="mt-5 pt-5 border-t border-gray-800">
                    <p className="text-[10px] font-black text-brand-muted uppercase tracking-widest mb-3">
                        {MONTH_NAMES[month]} {selectedDay}, {year}
                    </p>
                    {allSelectedOpps.length > 0 ? (
                        <div className="space-y-2">
                            {allSelectedOpps.map(opp => (
                                <div
                                    key={opp.id + opp._role}
                                    onClick={() => onViewOpportunity(opp)}
                                    className="flex items-center gap-3 p-3 bg-gray-800/40 rounded-xl border border-gray-700/50 cursor-pointer hover:border-brand-orange/40 transition-all"
                                >
                                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${opp._role === 'posted' ? 'bg-brand-orange' : 'bg-blue-400'}`} />
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm font-bold text-white truncate uppercase tracking-tight">{opp.title}</p>
                                        <p className="text-[10px] text-brand-muted uppercase tracking-widest">
                                            {opp._role === 'posted' ? 'Your event' : 'Attending'} · {opp.location}
                                        </p>
                                    </div>
                                    <span className="text-[10px] text-brand-orange font-black uppercase tracking-widest flex-shrink-0">→</span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-center text-brand-muted text-sm italic py-3">No events on this day.</p>
                    )}
                </div>
            )}

            {postedWithDates.length === 0 && attendingWithDates.length === 0 && (
                <p className="text-center text-brand-muted text-sm italic pt-4">No dated events yet — add a start date when posting or attend an event to see it here.</p>
            )}
        </div>
    );
};

export default ProDashboard;
